var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Button, Card, CircularProgress, Grid, IconButton, MenuItem, Popper, TextField } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useEffect, useRef, useState } from "react";
import { useSyncedRef } from "../../../../misc/SyncedRef";
import { red } from "@mui/material/colors";
import { distinct } from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export function AutoComplete(props) {
    const { search, value, equals, makeLabel, onSelect, onCreate, popperClassName } = props, textProps = __rest(props, ["search", "value", "equals", "makeLabel", "onSelect", "onCreate", "popperClassName"]);
    const [searchStr, setSearchStr] = useState("");
    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [list, setList] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => { if (open)
        return; setSearchStr(props.value ? makeLabel(props.value) || "" : ""); }, [props.value, makeLabel, open]);
    const searchRef = useSyncedRef(search);
    useEffect(() => { if (!open)
        return; let ignore = false; (() => __awaiter(this, void 0, void 0, function* () { try {
        setError(null);
        setLoading(true);
        const promise = yield searchRef.current(searchStr);
        if (ignore)
            return;
        setLoading(false);
        setList(promise);
    }
    catch (e) {
        if (ignore)
            return;
        setLoading(false);
        setError(e.toString());
    } }))(); return () => { ignore = true; }; }, [searchStr, open, searchRef]);
    const showCreate = (!list || list.filter(l => makeLabel(l) === searchStr).length === 0) && props.onCreate && searchStr;
    const [focused, setFocused] = useState(false);
    const [selected, setSelected] = useState([]);
    useEffect(() => {
        if (selected.length > 0)
            return;
        if (focused) {
            setOpen(focused);
            return;
        } // delay closing the popper to allow the user to click on the popper
        const tm = setTimeout(() => { setOpen(focused); }, 500);
        return () => clearTimeout(tm);
    }, [focused, selected]);
    const shiftKey = useRef(false);
    const ctrlKey = useRef(false);
    useEffect(() => { setSelected([]); }, [list]);
    useEffect(() => { const keydown = e => { if (e.key === "Shift") {
        shiftKey.current = true;
    } if (e.key === "Control" || e.key === "Meta") {
        ctrlKey.current = true;
    } }; const keyup = e => { if (e.key === "Shift") {
        shiftKey.current = false;
    } if (e.key === "Control" || e.key === "Meta") {
        ctrlKey.current = false;
    } }; document.addEventListener("keydown", keydown); document.addEventListener("keyup", keyup); return () => { document.removeEventListener("keydown", keydown); document.removeEventListener("keyup", keyup); }; }, []);
    const win = useWindowSize();
    return /*#__PURE__*/ _jsxs(_Fragment, { children: [props.useNativeInput ? /*#__PURE__*/ _jsx("input", { type: "text", value: searchStr, onChange: e => setSearchStr(e.target.value), ref: setAnchorEl, placeholder: props.placeholder, style: Object.assign({ border: "none", width: "100%", height: "100%", padding: 4, paddingLeft: 6, paddingRight: 6 }, props.style || {}), onFocus: () => setFocused(true), onBlur: () => setFocused(false), className: props.className, autoComplete: "off" }) : /*#__PURE__*/ _jsx(TextField, Object.assign(Object.assign({ value: searchStr, onChange: e => setSearchStr(e.target.value), ref: setAnchorEl, onFocus: () => setFocused(true), onBlur: () => setFocused(false) }, textProps), { autoComplete: "off", InputProps: Object.assign(Object.assign({}, textProps.InputProps), { endAdornment: /*#__PURE__*/ _jsx(IconButton, { size: "small" // don't steal focus from the input (which would blur/close it)
                        ,
                        onMouseDown: e => e.preventDefault(), onClick: () => {
                            if (open) {
                                setFocused(false);
                                setOpen(false);
                                return;
                            } // open with a blank search to show all options
                            setSearchStr("");
                            setFocused(true);
                            setOpen(true);
                        }, children: /*#__PURE__*/ _jsx(ArrowDropDownIcon, {}) }) }) })), /*#__PURE__*/ _jsx(Popper, { open: open, style: { zIndex: 1400 }, anchorEl: anchorEl, placement: "bottom-start", disablePortal: false, modifiers: [{ name: 'flip', enabled: true, options: { altBoundary: true, rootBoundary: 'viewport', padding: 8 } }, { name: 'preventOverflow', enabled: true, options: { boundary: "viewport", altAxis: true, altBoundary: true, tether: true, rootBoundary: 'viewport', padding: 8 } }], children: /*#__PURE__*/ _jsxs(Card, { className: props.popperClassName, style: { width: anchorEl && window.getComputedStyle(anchorEl).width }, children: [loading && /*#__PURE__*/ _jsx(CircularProgress, {}), /*#__PURE__*/ _jsxs("div", { style: { flex: 1, overflow: "auto", maxHeight: win.height * 3 / 4 }, children: [!loading && error && /*#__PURE__*/ _jsx(MenuItem, { style: { color: red["700"] }, children: error }), list && list.map((l, index) => /*#__PURE__*/ _jsx(MenuItem, { selected: selected.indexOf(l) !== -1, onClick: e => {
                                        if (props.onMultiSelect) {
                                            if (shiftKey.current) {
                                                if (selected.length > 0) {
                                                    const lastItemIndex = list.indexOf(selected[selected.length - 1]);
                                                    const toAdd = makeRange(lastItemIndex, index).map(i => { return list[i]; });
                                                    setSelected(distinct([...selected, ...toAdd]));
                                                }
                                                else {
                                                    setSelected([l]);
                                                }
                                                return;
                                            }
                                            if (ctrlKey.current) { // multi select
                                                setSelected(distinct([...selected, l]));
                                                return;
                                            }
                                        }
                                        onSelect(l);
                                    }, children: (props.makeLabelReact || makeLabel)(l) }, index.toString())), showCreate && /*#__PURE__*/ _jsxs(MenuItem, { onClick: () => { console.log("create", searchStr); if (onCreate)
                                        onCreate(searchStr); }, children: ["Create '", searchStr, "'"] }), (!list || list.length === 0) && !showCreate && /*#__PURE__*/ _jsx(MenuItem, { children: /*#__PURE__*/ _jsx("div", { style: { fontSize: "0.8rem", textAlign: "center" }, children: "No results" }) }, "no-result")] }), selected.length > 0 && /*#__PURE__*/ _jsx("div", { style: { padding: 8 }, children: /*#__PURE__*/ _jsxs(Grid, { container: true, spacing: 1, justifyContent: "flex-end", children: [/*#__PURE__*/ _jsx(Grid, { item: true, children: /*#__PURE__*/ _jsx(Button, { size: "small", color: "primary", onClick: () => setSelected([]), children: "Cancel" }) }), /*#__PURE__*/ _jsx(Grid, { item: true, children: /*#__PURE__*/ _jsx(Button, { size: "small", color: "primary", variant: "contained", onClick: () => { if (props.onMultiSelect)
                                                props.onMultiSelect(selected); setSelected([]); }, children: "Submit" }) })] }) })] }) })] });
}
function makeRange(a, b) { if (b < a) {
    let tmp = a;
    a = b;
    b = tmp;
} const rs = []; for (let i = a; i <= b; i++) {
    rs.push(i);
} return rs; }
